import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { WriteFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';

type HookStatus = 'done' | 'error' | 'saving';
type SaveEntityFn<TRead, TWrite> = (
  updateRequest: TWrite,
  onComplete?: (newEntity: TRead) => void,
  onError?: (error: BaseError) => void,
) => void;

interface WritePatternDoneHook<TRead, TWrite> {
  status: 'done';
  saved: boolean;
  entity: TRead | null;
  saveEntity: SaveEntityFn<TRead, TWrite>;
  saveIndicatorStatus: 'idle' | 'saved';
  canWrite: boolean;
}

interface WritePatternErrorHook<TRead, TWrite> {
  status: 'error';
  error: BaseError;
  saveEntity: SaveEntityFn<TRead, TWrite>;
  saveIndicatorStatus: 'idle';
  canWrite: boolean;
}

interface WritePatternSavingHook<TWrite> {
  status: 'saving';
  updateRequest: TWrite;
  saveIndicatorStatus: 'saving';
  canWrite: false;
}

export type WritePatternHook<TRead, TWrite> =
  | WritePatternDoneHook<TRead, TWrite>
  | WritePatternSavingHook<TWrite>
  | WritePatternErrorHook<TRead, TWrite>;

interface SaveTransaction<TWrite> {
  updateRequest: TWrite;
}

export const useWritePattern = <TRead, TWrite>({
  isReadyToWrite,
  writeFn,
}: {
  isReadyToWrite: boolean;
  writeFn: WriteFn<TRead, TWrite>;
}): WritePatternHook<TRead, TWrite> => {
  const saved = useRef<boolean>(false);
  const entity = useRef<TRead | null>(null);
  const [status, setStatus] = useState<HookStatus>('done');
  const [error, setError] = useState<BaseError | null>(null);
  const saveTransaction = useRef<SaveTransaction<TWrite> | null>(null);

  const transitionToError = useCallback((error: BaseError) => {
    saveTransaction.current = null;
    setStatus('error');
    setError(error);
  }, []);

  const transitionToDone = useCallback(
    (loadedEntity: TRead, wasSaved: boolean) => {
      saveTransaction.current = null;
      saved.current = wasSaved;
      entity.current = loadedEntity;
      setStatus('done');
    },
    [],
  );

  const transitionToSaving = useCallback((updateRequest: TWrite) => {
    saveTransaction.current = { updateRequest };
    setStatus('saving');
  }, []);

  const saveEntity = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      updateRequest: TWrite,
      onComplete?: (newEntity: TRead) => void,
      onError?: (error: BaseError) => void,
    ) => {
      if (!isAlive() || !isReadyToWrite || saveTransaction.current) {
        return;
      }

      transitionToSaving(updateRequest);

      writeFn(
        updateRequest,
        (writtenEntity) => {
          if (!isAlive()) {
            return;
          }

          transitionToDone(writtenEntity, true);
          if (onComplete) {
            onComplete(writtenEntity);
          }
        },
        (error) => {
          if (!isAlive()) {
            return;
          }

          transitionToError(error);

          if (onError) {
            onError(error);
          }
        },
      );
    },
    [
      isReadyToWrite,
      transitionToDone,
      transitionToError,
      transitionToSaving,
      writeFn,
    ],
  );

  return useMemo(() => {
    if (status === 'error') {
      return {
        status,
        error: error ?? {
          errorType: 'UnknownError',
          errorReason: 'Unknown error',
        },
        saveEntity,
        saveIndicatorStatus: 'idle',
        canWrite: isReadyToWrite,
      };
    }

    if (status === 'saving') {
      if (!saveTransaction.current) {
        throw new Error('saveTransaction.current is required');
      }

      return {
        status,
        updateRequest: saveTransaction.current.updateRequest,
        saveIndicatorStatus: 'saving',
        canWrite: false,
      };
    }

    if (status === 'done') {
      return {
        status,
        saved: saved.current,
        entity: entity.current,
        saveIndicatorStatus: saved.current ? 'saved' : 'idle',
        saveEntity,
        canWrite: isReadyToWrite,
      };
    }

    throw guardExhaustive(status);
  }, [error, isReadyToWrite, saveEntity, status]);
};
